import * as React from 'react';
import { useNavigate } from 'react-router-dom';

import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import Dialog from '@mui/material/Dialog';
import FormControlLabel from '@mui/material/FormControlLabel';
import IconButton from '@mui/material/IconButton';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import AddIcon from '@mui/icons-material/Add';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import MyLocationIcon from '@mui/icons-material/MyLocation';
import CloseIcon from '@mui/icons-material/Close';
import AccountBalanceWalletOutlinedIcon from '@mui/icons-material/AccountBalanceWalletOutlined';
import CardGiftcardOutlinedIcon from '@mui/icons-material/CardGiftcardOutlined';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import BadgeOutlinedIcon from '@mui/icons-material/BadgeOutlined';
import Switch from '@mui/material/Switch';
import RemoveIcon from '@mui/icons-material/Remove';

import { currency } from '../ui';
import { PublicBrand, fontStack } from './brand';
import { Ordering, computeFee, hasPaidRange, nextFeeStep, useCart } from './cart';
import { forgetCard, savedCard } from './savedCard';
import { Promotion, useQuote } from './quote';

type Cart = ReturnType<typeof useCart>;
type Fulfillment = 'pickup' | 'delivery';
type Pay = 'online' | 'later';

const CUSTOMER_KEY = 'true.customer';
const remembered = () => {
  try {
    return JSON.parse(localStorage.getItem(CUSTOMER_KEY) || '{}') as { name?: string; phone?: string; address?: string };
  } catch {
    return {};
  }
};

/** The cart and its checkout: lines, pickup or delivery (with its fee), the customer's name and phone, and sending it. */
const CartDialog = ({ open, onClose, slug, businessName, brand, ordering, cart, promotions = [] }: {
  open: boolean; onClose: () => void; slug: string; businessName: string; brand: PublicBrand; ordering: Ordering; cart: Cart;
  /** The business's promos that apply by themselves (the catalog lists them): the checkout asks what they take off. */
  promotions?: Promotion[];
}) => {
  const navigate = useNavigate();
  const c = brand.colors;
  const heading = { fontFamily: fontStack(brand.fonts.heading), fontStretch: brand.fonts.heading.stretch ?? 'normal' };
  const saved = React.useMemo(remembered, []);
  const [fulfillment, setFulfillment] = React.useState<Fulfillment>(ordering.pickup ? 'pickup' : 'delivery');
  const [name, setName] = React.useState(saved.name ?? '');
  const [phone, setPhone] = React.useState(saved.phone ?? '');
  const [address, setAddress] = React.useState(saved.address ?? '');
  const [notes, setNotes] = React.useState('');
  const [optIn, setOptIn] = React.useState(false);
  const [website, setWebsite] = React.useState('');
  const [errors, setErrors] = React.useState<Record<string, string>>({});
  const [sending, setSending] = React.useState(false);
  const [request, setRequest] = React.useState('');
  // Paying online when the business offers it (its provider's page, then back to the order).
  const [pay, setPay] = React.useState<Pay>('online');
  // Where to deliver, from the phone's location (optional): the business gets a map link.
  const [location, setLocation] = React.useState<{ lat: number; lng: number; accuracy: number } | null>(null);
  const [locating, setLocating] = React.useState(false);
  const [locationError, setLocationError] = React.useState('');
  const locate = () => {
    if (!('geolocation' in navigator)) { setLocationError('Tu navegador no comparte la ubicación: escribe tu dirección.'); return; }
    setLocating(true);
    setLocationError('');
    navigator.geolocation.getCurrentPosition(
      ({ coords }) => { setLocation({ lat: coords.latitude, lng: coords.longitude, accuracy: Math.round(coords.accuracy) }); setLocating(false); },
      error => {
        setLocating(false);
        setLocationError(error.code === error.PERMISSION_DENIED
          ? 'No diste permiso de ubicación. Actívalo en tu navegador o escribe tu dirección.'
          : 'No pudimos obtener tu ubicación. Vuelve a intentar o escribe tu dirección.');
      },
      { enableHighAccuracy: true, timeout: 15000, maximumAge: 60000 },
    );
  };

  const fee = fulfillment === 'delivery' ? computeFee(ordering.delivery_fee, cart.subtotal, cart.count) : 0;
  const serviceFee = computeFee(ordering.service_fee, cart.subtotal, cart.count);
  // Like a delivery app: how much more for free (or cheaper) delivery.
  const step = fulfillment === 'delivery' ? nextFeeStep(ordering.delivery_fee, cart.subtotal, cart.count) : null;
  const freeDelivery = fulfillment === 'delivery' && fee === 0 && hasPaidRange(ordering.delivery_fee);
  // The business's promos and the customer's reward (api/promotions.py): asked to the server as the checkout changes.
  // Rewards come with their card on this phone (kept when they open its link; their phone or name alone never do);
  // the one worth the most is chosen until they pick another one (or none).
  const cardCode = React.useMemo(() => savedCard(slug), [slug]);
  const lines = cart.lines.map(l => (l.product == null
    ? { name: l.name, quantity: l.quantity }
    : { product: l.product, quantity: l.quantity, options: l.options.map(o => o.id) }));
  const [attempt, setAttempt] = React.useState(0);
  const { quote, current } = useQuote(slug, open && lines.length > 0 && (promotions.length > 0 || !!ordering.rewards), {
    customer: { name, phone }, fulfillment, items: lines, ...(cardCode ? { card: cardCode } : {}), attempt,
  });
  const [chosenReward, setChosenReward] = React.useState<number | null | undefined>(undefined);
  const rewards = quote?.rewards ?? [];
  const pickedId = chosenReward === undefined ? quote?.best_reward ?? null : chosenReward;
  const reward = rewards.find(r => r.id === pickedId && r.usable) ?? null;
  const discount = quote?.discount ?? 0;
  const rewardAmount = reward?.amount ?? 0;
  const total = Math.round((cart.subtotal - discount - rewardAmount + fee + serviceFee) * 100) / 100;
  // The business confirms what things cost once it buys them: the total is an estimate until then.
  const estimate = !!ordering.estimated_prices || cart.unpriced > 0;
  const addRequest = () => {
    const name = request.trim().replace(/\s+/g, ' ');
    if (name.length < 2) return;
    cart.add({ product: null, name: name.slice(0, 120), unit: 0, options: [], quantity: 1 });
    setRequest('');
  };
  const short = (ordering.min_order ?? 0) > cart.subtotal;

  // Their saldo: the card they opened on this phone, while the business takes it for orders.
  const card = React.useMemo(() => (ordering.balance ? savedCard(slug) : null), [ordering.balance, slug]);
  const [wallet, setWallet] = React.useState<{ balance: number } | null>(null);
  const [useBalance, setUseBalance] = React.useState(true);
  React.useEffect(() => {
    if (!open || !card) return;
    let alive = true;
    fetch(`${process.env.REACT_APP_BASE_URL}/api/public/businesses/${encodeURIComponent(slug)}/mi/${encodeURIComponent(card)}/`)
      .then(async response => {
        if (!alive) return;
        if (response.status === 404) { forgetCard(slug); return; }
        const data = await response.json().catch(() => null);
        const balance = data?.balance;
        setWallet(balance?.orders && balance.balance > 0 ? { balance: balance.balance } : null);
      })
      .catch(() => {});
    return () => { alive = false; };
  }, [open, card, slug]);
  const fromBalance = wallet && useBalance && !estimate ? Math.min(wallet.balance, total) : 0;
  const rest = Math.round((total - fromBalance) * 100) / 100;
  const coveredByBalance = fromBalance > 0 && rest <= 0;

  const payRequired = ordering.pay_online_required === 'always' || (ordering.pay_online_required === 'delivery' && fulfillment === 'delivery');
  const paysOnline = !!ordering.pay_online && !estimate && !coveredByBalance && (payRequired || pay === 'online');

  const send = async (e: React.FormEvent) => {
    e.preventDefault();
    setSending(true);
    setErrors({});
    try {
      const response = await fetch(`${process.env.REACT_APP_BASE_URL}/api/public/businesses/${encodeURIComponent(slug)}/orders/`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          customer: { name, phone, marketing_opt_in: optIn }, fulfillment, delivery_address: fulfillment === 'delivery' ? address : '',
          pay: paysOnline ? 'online' : 'later',
          ...(cardCode ? { card: cardCode } : {}),
          ...(reward ? { reward: reward.id } : {}),
          ...(fromBalance > 0 && card ? { use_balance: true, card } : {}),
          ...(fulfillment === 'delivery' && location ? { delivery_location: location } : {}),
          notes, website, items: lines,
        }),
      });
      const data = await response.json().catch(() => ({}));
      if (response.status === 201) {
        try {
          localStorage.setItem(CUSTOMER_KEY, JSON.stringify({ name, phone, address }));
        } catch { /* nothing to remember in a private window */ }
        cart.clear();
        // Paying online: to the provider's page, which brings them back to the order.
        if (data.payment_url) {
          window.location.href = data.payment_url;
          return;
        }
        onClose();
        navigate(data.track_path, { state: { placed: true, notice: data.payment_problem } });
        return;
      }
      // Their reward was used meanwhile (another order, at the register): the quote comes again without it.
      if (response.status === 409 && data?.reward) { setChosenReward(undefined); setAttempt(n => n + 1); }
      if (response.status === 429) setErrors({ detail: 'Demasiados pedidos seguidos. Espera un momento y vuelve a intentar.' });
      else if (typeof data?.detail === 'string') setErrors({ detail: data.detail });
      else setErrors(Object.fromEntries(Object.entries(data ?? {}).map(([k, v]) => [k, Array.isArray(v) ? v.join(' ') : String(v)])));
    } catch {
      setErrors({ detail: 'No hay conexión. Vuelve a intentar.' });
    } finally {
      setSending(false);
    }
  };

  const field = { '& .MuiOutlinedInput-root': { bgcolor: c.surface, borderRadius: '12px', fontFamily: 'inherit' }, '& label': { fontFamily: 'inherit' } };
  const choice = (value: Fulfillment, label: string) => (
    <Button key={value} onClick={() => setFulfillment(value)} variant={fulfillment === value ? 'contained' : 'outlined'}
      sx={{ flex: 1, py: 1, borderRadius: 99, textTransform: 'none', fontWeight: 700, fontFamily: 'inherit', boxShadow: 'none',
        ...(fulfillment === value ? { bgcolor: c.primary, color: c.on_primary, '&:hover': { bgcolor: c.primary, boxShadow: 'none' } }
          : { color: c.text, borderColor: c.border }) }}>
      {label}
    </Button>
  );

  const payChoice = (value: Pay, label: string) => (
    <Button key={value} onClick={() => setPay(value)} variant={pay === value ? 'contained' : 'outlined'} data-testid={`pay-${value}`}
      sx={{ flex: 1, py: 1, borderRadius: 99, textTransform: 'none', fontWeight: 700, fontFamily: 'inherit', boxShadow: 'none',
        ...(pay === value ? { bgcolor: c.primary, color: c.on_primary, '&:hover': { bgcolor: c.primary, boxShadow: 'none' } }
          : { color: c.text, borderColor: c.border }) }}>
      {label}
    </Button>
  );

  const panel = { bgcolor: c.surface, border: `1px solid ${c.border}`, borderRadius: '16px' };
  const pieces = cart.lines.reduce((sum, l) => sum + l.quantity, 0);
  return (
    // Like an online store's cart: what you're taking on the left, the checkout on a sidebar (below it on phones).
    <Dialog open={open} onClose={onClose} fullScreen slotProps={{ paper: { sx: { bgcolor: c.background, color: c.text } } }}>
      <Box component="form" onSubmit={send} noValidate data-testid="checkout"
        sx={{ fontFamily: fontStack(brand.fonts.body), fontStretch: brand.fonts.body.stretch ?? 'normal', minHeight: '100%' }}>
        <Box sx={{ position: 'sticky', top: 0, zIndex: 2, bgcolor: c.surface, borderBottom: `1px solid ${c.border}` }}>
          <Box sx={{ maxWidth: 1120, mx: 'auto', px: { xs: 1, md: 3 }, py: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
            <Button onClick={onClose} startIcon={<ArrowBackIcon />}
              sx={{ textTransform: 'none', fontWeight: 700, fontFamily: 'inherit', color: c.text, borderRadius: 99, px: 1.5 }}>
              Seguir comprando
            </Button>
            <Box sx={{ flex: 1 }} />
            {brand.logo
              ? <Box component="img" src={brand.logo} alt={businessName} sx={{ height: 36, maxWidth: 140, objectFit: 'contain' }} />
              : <Typography sx={{ fontWeight: 800, ...heading }}>{businessName}</Typography>}
            <IconButton onClick={onClose} aria-label="Cerrar" sx={{ color: c.text, display: { xs: 'none', md: 'inline-flex' } }}><CloseIcon /></IconButton>
          </Box>
        </Box>

        <Box sx={{ maxWidth: 1120, mx: 'auto', px: { xs: 2, md: 3 }, py: { xs: 2, md: 3 }, display: 'grid', gap: { xs: 2, md: 3 },
          gridTemplateColumns: { xs: 'minmax(0, 1fr)', md: 'minmax(0, 1fr) 380px' }, alignItems: 'start' }}>
          {/* The cart */}
          <Box>
            <Typography component="h2" sx={{ fontWeight: 800, fontSize: { xs: 24, md: 30 }, mb: 1.5, ...heading }}>Tu carrito</Typography>
            {ordering.free_text && (
              <Box sx={{ display: 'flex', gap: 1, mb: 1.5 }}>
                <TextField size="small" fullWidth value={request} onChange={e => setRequest(e.target.value)} sx={field}
                  label="¿No está en el catálogo? Escríbelo" placeholder="p. ej. Croissants 12 pzas"
                  onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addRequest(); } }}
                  slotProps={{ htmlInput: { maxLength: 120, 'data-testid': 'request-input' } }} />
                <Button onClick={addRequest} variant="outlined" disabled={request.trim().length < 2} data-testid="request-add"
                  sx={{ flexShrink: 0, borderRadius: 99, textTransform: 'none', fontWeight: 700, fontFamily: 'inherit', color: c.text, borderColor: c.border }}>
                  Agregar
                </Button>
              </Box>
            )}
            {cart.lines.length === 0 ? (
              <Box sx={{ ...panel, p: 4, textAlign: 'center' }}>
                <Typography sx={{ color: c.muted, fontFamily: 'inherit', mb: 2 }}>Tu carrito está vacío.</Typography>
                <Button onClick={onClose} variant="contained" sx={{ borderRadius: 99, textTransform: 'none', fontWeight: 700, fontFamily: 'inherit',
                  bgcolor: c.primary, color: c.on_primary, boxShadow: 'none', '&:hover': { bgcolor: c.primary, boxShadow: 'none' } }}>
                  Ver el {brand.catalog_label?.toLowerCase() || 'menú'}
                </Button>
              </Box>
            ) : (
              <Box sx={panel}>
                <Typography sx={{ display: { xs: 'none', md: 'block' }, textAlign: 'right', color: c.muted, fontSize: 13, px: 2, pt: 1.5, fontFamily: 'inherit' }}>Precio</Typography>
                {cart.lines.map((line, i) => (
                  <Box key={line.key} data-testid="cart-line"
                    sx={{ display: 'flex', gap: 2, alignItems: 'flex-start', px: 2, py: 1.75, borderTop: i ? `1px solid ${c.border}` : { xs: 'none', md: `1px solid ${c.border}` } }}>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography sx={{ fontWeight: 700, fontSize: 17, fontFamily: 'inherit' }}>{line.name}</Typography>
                      {line.options.length > 0 && (
                        <Typography sx={{ color: c.muted, fontSize: 13.5, fontFamily: 'inherit' }}>{line.options.map(o => o.name).join(', ')}</Typography>
                      )}
                      <Typography sx={{ color: c.muted, fontSize: 13.5, fontFamily: 'inherit' }}>
                        {line.product == null ? 'Por cotizar' : `${currency(line.unit)} c/u${ordering.estimated_prices ? ' aprox.' : ''}`}
                      </Typography>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mt: 1, flexWrap: 'wrap' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', border: `1px solid ${c.border}`, borderRadius: 99, bgcolor: c.background }}>
                          <IconButton size="small" aria-label={line.quantity === 1 ? 'Quitar' : 'Uno menos'} onClick={() => cart.setQuantity(line.key, line.quantity - 1)} sx={{ color: c.text }}>
                            <RemoveIcon fontSize="small" />
                          </IconButton>
                          <Typography sx={{ minWidth: 24, textAlign: 'center', fontWeight: 700, fontFamily: 'inherit' }}>{line.quantity}</Typography>
                          <IconButton size="small" aria-label="Uno más" onClick={() => cart.setQuantity(line.key, line.quantity + 1)} sx={{ color: c.text }}>
                            <AddIcon fontSize="small" />
                          </IconButton>
                        </Box>
                        <Button size="small" onClick={() => cart.setQuantity(line.key, 0)}
                          sx={{ textTransform: 'none', fontFamily: 'inherit', color: c.muted, minWidth: 0, px: 0.5, textDecoration: 'underline' }}>
                          Eliminar
                        </Button>
                      </Box>
                    </Box>
                    <Typography sx={{ fontWeight: 800, fontSize: 17, fontFamily: 'inherit', flexShrink: 0 }}>
                      {line.product == null ? '—' : currency(line.unit * line.quantity)}
                    </Typography>
                  </Box>
                ))}
                <Box sx={{ px: 2, py: 1.75, borderTop: `1px solid ${c.border}`, textAlign: 'right' }}>
                  <Typography sx={{ fontSize: 17, fontFamily: 'inherit' }}>
                    Subtotal ({pieces} {pieces === 1 ? 'producto' : 'productos'}): <strong>{currency(cart.subtotal)}{cart.unpriced ? ' + por cotizar' : ''}</strong>
                  </Typography>
                </Box>
              </Box>
            )}
          </Box>

          {/* The checkout */}
          {cart.lines.length > 0 && (
            <Box sx={{ ...panel, p: 2, position: { md: 'sticky' }, top: { md: 76 } }}>
              <Typography component="h3" sx={{ fontWeight: 800, fontSize: 20, mb: 1.5, ...heading }}>Finaliza tu pedido</Typography>
              {ordering.pickup && ordering.delivery && (
                <Box sx={{ display: 'flex', gap: 1 }}>{[choice('pickup', 'Recoger en tienda'), choice('delivery', 'A domicilio')]}</Box>
              )}
              {!(ordering.pickup && ordering.delivery) && (
                <Typography sx={{ fontWeight: 600, fontFamily: 'inherit' }}>{ordering.pickup ? 'Recoges en tienda.' : 'Te lo llevamos a domicilio.'}</Typography>
              )}
              {errors.fulfillment && <Typography role="alert" sx={{ color: '#C0392B', fontSize: 13, mt: 0.5, fontFamily: 'inherit' }}>{errors.fulfillment}</Typography>}
              {(step || freeDelivery) && (
                <Box data-testid="delivery-nudge" sx={{ mt: 1.5, p: 1.5, borderRadius: '12px', bgcolor: freeDelivery ? c.accent : c.background,
                  color: freeDelivery ? c.on_accent : c.text, border: freeDelivery ? 'none' : `1.5px dashed ${c.primary}` }}>
                  <Typography sx={{ fontWeight: 700, fontFamily: 'inherit' }}>
                    {step
                      ? <>Agrega {currency(step.missing)} más a tu pedido y {step.fee ? `tu envío cuesta ${currency(step.fee)}` : 'tu envío es gratis'}.</>
                      : '¡Tu envío es gratis!'}
                  </Typography>
                  {step && (
                    <>
                      <Box sx={{ height: 6, borderRadius: 3, bgcolor: c.border, overflow: 'hidden', my: 1 }} aria-hidden>
                        <Box sx={{ height: '100%', width: `${Math.min(100, Math.round((cart.subtotal / step.from) * 100))}%`, bgcolor: c.primary, transition: 'width 200ms' }} />
                      </Box>
                      <Button onClick={onClose} sx={{ p: 0, minWidth: 0, textTransform: 'none', fontWeight: 700, fontFamily: 'inherit', color: c.text, textDecoration: 'underline' }}>
                        Agregar algo más
                      </Button>
                    </>
                  )}
                </Box>
              )}

              <Box sx={{ display: 'grid', gap: 1.5, mt: 2 }}>
                {fulfillment === 'delivery' && (
                  <Box data-testid="delivery-location" sx={{ display: 'grid', gap: 0.75 }}>
                    {location ? (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, p: 1.25, borderRadius: '12px', bgcolor: c.background, border: `1px solid ${c.border}` }}>
                        <MyLocationIcon fontSize="small" sx={{ color: c.primary }} />
                        <Typography sx={{ flex: 1, fontSize: 14, fontFamily: 'inherit' }}>
                          Ubicación compartida{location.accuracy ? ` (± ${location.accuracy} m)` : ''} ·{' '}
                          <Box component="a" href={`https://www.google.com/maps/search/?api=1&query=${location.lat},${location.lng}`} target="_blank" rel="noopener noreferrer" sx={{ color: 'inherit' }}>ver en el mapa</Box>
                        </Typography>
                        <Button size="small" onClick={() => setLocation(null)} sx={{ textTransform: 'none', fontFamily: 'inherit', color: c.muted, minWidth: 0 }}>Quitar</Button>
                      </Box>
                    ) : (
                      <Button onClick={locate} disabled={locating} variant="outlined" startIcon={<MyLocationIcon />} data-testid="use-location"
                        sx={{ borderRadius: 99, textTransform: 'none', fontWeight: 700, fontFamily: 'inherit', color: c.text, borderColor: c.border, py: 1 }}>
                        {locating ? 'Obteniendo tu ubicación…' : 'Usar mi ubicación actual'}
                      </Button>
                    )}
                    {locationError && <Typography role="alert" sx={{ color: '#B26A00', fontSize: 13, fontFamily: 'inherit' }}>{locationError}</Typography>}
                  </Box>
                )}
                {fulfillment === 'delivery' && (
                  <TextField label={location ? 'Referencias para encontrarte (opcional)' : 'Dirección de entrega'} value={address}
                    onChange={e => setAddress(e.target.value)} multiline minRows={2} required={!location}
                    error={!!errors.delivery_address}
                    helperText={errors.delivery_address || (location ? 'Calle y número, color de la casa, entre qué calles…' : 'Calle, número, colonia y referencias.')} sx={field}
                    slotProps={{ htmlInput: { maxLength: 300 } }} />
                )}
                <TextField label="Nombre" value={name} onChange={e => setName(e.target.value)} required autoComplete="name"
                  error={!!errors.name} helperText={errors.name} sx={field} slotProps={{ htmlInput: { maxLength: 60 } }} />
                <TextField label="Celular" value={phone} onChange={e => setPhone(e.target.value)} required type="tel" autoComplete="tel"
                  error={!!errors.phone} helperText={errors.phone || 'Para avisarte de tu pedido.'} sx={field}
                  slotProps={{ htmlInput: { inputMode: 'tel', maxLength: 20 } }} />
                <TextField label="Indicaciones (opcional)" value={notes} onChange={e => setNotes(e.target.value)} multiline minRows={1} sx={field}
                  slotProps={{ htmlInput: { maxLength: 500 } }} />
                {/* Never shown to people: a bot that fills every field fills this one. */}
                <Box component="input" tabIndex={-1} autoComplete="off" aria-hidden name="website" value={website}
                  onChange={e => setWebsite((e.target as HTMLInputElement).value)}
                  sx={{ position: 'absolute', left: '-10000px', width: '1px', height: '1px', opacity: 0 }} />
                <FormControlLabel sx={{ alignItems: 'flex-start', m: 0 }}
                  control={<Checkbox checked={optIn} onChange={e => setOptIn(e.target.checked)} sx={{ mt: -0.75, color: c.muted, '&.Mui-checked': { color: c.primary } }} />}
                  label={<Typography sx={{ fontSize: 14, fontFamily: 'inherit' }}>Quiero recibir promociones de {businessName} por WhatsApp.</Typography>} />
              </Box>

              {quote?.card && (
                <Box data-testid="cart-card" sx={{ mt: 2, display: 'flex', gap: 1, alignItems: 'flex-start', color: c.text }}>
                  <BadgeOutlinedIcon sx={{ color: c.primary, mt: 0.1 }} />
                  <Typography sx={{ fontSize: 14, fontFamily: 'inherit' }}>
                    Pides con tu tarjeta de cliente{quote.card.first_name ? `, ${quote.card.first_name}` : ''}.
                    {quote.card.phone_matches === false && (
                      <Box component="span" sx={{ display: 'block', color: '#B26A00', fontWeight: 600 }}>
                        Escribe el celular de tu tarjeta (termina en {quote.card.phone_end}) para usar tus recompensas.
                      </Box>
                    )}
                  </Typography>
                </Box>
              )}
              {rewards.length > 0 && (
                <Box data-testid="cart-rewards" sx={{ mt: 2 }}>
                  <Typography sx={{ fontWeight: 700, fontFamily: 'inherit', mb: 1 }}>{rewards.length === 1 ? 'Tu recompensa' : 'Tus recompensas'}</Typography>
                  <Box sx={{ display: 'grid', gap: 1 }}>
                    {rewards.map(r => {
                      const picked = reward?.id === r.id;
                      const until = new Date(r.expires_at).toLocaleDateString('es-MX', { day: 'numeric', month: 'short' });
                      return (
                        <Box key={r.id} component="button" type="button" disabled={!r.usable} data-testid="cart-reward" aria-pressed={picked}
                          onClick={() => setChosenReward(picked ? null : r.id)}
                          sx={{ all: 'unset', boxSizing: 'border-box', cursor: r.usable ? 'pointer' : 'default', p: 1.5, borderRadius: '14px',
                            display: 'flex', alignItems: 'center', gap: 1.25, opacity: r.usable ? 1 : 0.75,
                            bgcolor: picked ? c.accent : c.background, color: picked ? c.on_accent : c.text, border: picked ? 'none' : `1px solid ${c.border}`,
                            '&:focus-visible': { outline: `2px solid ${c.primary}`, outlineOffset: 2 } }}>
                          {picked ? <CheckCircleIcon /> : <CardGiftcardOutlinedIcon />}
                          <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Typography sx={{ fontWeight: 700, fontFamily: 'inherit' }}>{r.title}</Typography>
                            <Typography sx={{ fontSize: 13, opacity: 0.85, fontFamily: 'inherit' }}>
                              {r.usable
                                ? [r.amount > 0 ? `−${currency(r.amount)}` : '', r.gifts.length ? `Te damos: ${r.gifts.join(', ')}` : '', `vence el ${until}`].filter(Boolean).join(' · ')
                                : `${r.hint} Vence el ${until}.`}
                            </Typography>
                          </Box>
                          {r.usable && <Typography sx={{ fontSize: 13, fontWeight: 700, fontFamily: 'inherit' }}>{picked ? 'Usando' : 'Usar'}</Typography>}
                        </Box>
                      );
                    })}
                  </Box>
                  {reward && rewards.filter(r => r.usable).length > 1 && (
                    <Typography sx={{ color: c.muted, fontSize: 13, mt: 0.75, fontFamily: 'inherit' }}>Una recompensa por pedido.</Typography>
                  )}
                </Box>
              )}
              {errors.reward && <Typography role="alert" sx={{ color: '#C0392B', fontSize: 14, mt: 1, fontFamily: 'inherit' }}>{errors.reward}</Typography>}
              {ordering.rewards && (!cardCode || (quote && current && !quote.card)) && (
                <Typography sx={{ color: c.muted, fontSize: 13, mt: 1.5, fontFamily: 'inherit' }} data-testid="cart-rewards-hint">
                  ¿Ya tienes tu tarjeta de cliente de {businessName}? Ábrela con el enlace de tu ticket y haz tu pedido desde ahí para usar tus recompensas.
                </Typography>
              )}
              {wallet && !estimate && (
                <Box data-testid="cart-balance" sx={{ mt: 2, p: 1.5, borderRadius: '14px', display: 'flex', alignItems: 'center', gap: 1.25,
                  bgcolor: useBalance ? c.accent : c.background, color: useBalance ? c.on_accent : c.text, border: useBalance ? 'none' : `1px solid ${c.border}` }}>
                  <AccountBalanceWalletOutlinedIcon />
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography sx={{ fontWeight: 700, fontFamily: 'inherit' }}>Pagar con mi saldo</Typography>
                    <Typography sx={{ fontSize: 13, opacity: 0.85, fontFamily: 'inherit' }}>
                      Tienes {currency(wallet.balance)}. Usa el celular de tu tarjeta.
                    </Typography>
                  </Box>
                  <Switch checked={useBalance} onChange={e => setUseBalance(e.target.checked)} slotProps={{ input: { 'aria-label': 'Pagar con mi saldo' } }}
                    sx={{ '& .MuiSwitch-switchBase.Mui-checked': { color: c.on_accent }, '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { bgcolor: c.on_accent } }} />
                </Box>
              )}
              {errors.balance && <Typography role="alert" sx={{ color: '#C0392B', fontSize: 14, mt: 1, fontFamily: 'inherit' }}>{errors.balance}</Typography>}
              {ordering.balance && !card && (
                <Typography sx={{ color: c.muted, fontSize: 13, mt: 1.5, fontFamily: 'inherit' }}>
                  ¿Tienes saldo en {businessName}? Abre tu tarjeta (el enlace de tu ticket) en este teléfono para pagar con él.
                </Typography>
              )}
              <Box sx={{ mt: 2, pt: 1.5, borderTop: `1px solid ${c.border}`, fontFamily: 'inherit' }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span>Productos ({pieces})</span><span>{currency(cart.subtotal)}{cart.unpriced ? ' + por cotizar' : ''}</span>
                </Box>
                {(quote?.promotions ?? []).map(promo => (
                  <Box key={promo.id} data-testid="cart-promotion" sx={{ display: 'flex', justifyContent: 'space-between', gap: 1, mt: 0.5 }}>
                    <span>{promo.name}</span><span>−{currency(promo.amount)}</span>
                  </Box>
                ))}
                {reward && rewardAmount > 0 && (
                  <Box data-testid="cart-reward-used" sx={{ display: 'flex', justifyContent: 'space-between', gap: 1, mt: 0.5 }}>
                    <span>Recompensa: {reward.title}</span><span>−{currency(rewardAmount)}</span>
                  </Box>
                )}
                {fulfillment === 'delivery' && (
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 0.5 }}><span>Envío</span><span>{fee ? currency(fee) : 'Gratis'}</span></Box>
                )}
                {serviceFee > 0 && (
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 0.5 }}><span>{ordering.service_fee_label || 'Comisión por servicio'}</span><span>{currency(serviceFee)}</span></Box>
                )}
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1, fontWeight: 800, fontSize: 20 }}>
                  <span>{estimate ? 'Total aproximado' : 'Total'}</span><span>{cart.unpriced ? 'Por confirmar' : currency(total)}</span>
                </Box>
                {fromBalance > 0 && (
                  <>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 0.5 }} data-testid="cart-balance-used">
                      <span>Con tu saldo</span><span>−{currency(fromBalance)}</span>
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 0.5, fontWeight: 800 }}>
                      <span>Por pagar</span><span>{currency(rest)}</span>
                    </Box>
                  </>
                )}
                <Typography sx={{ color: c.muted, fontSize: 13, mt: 0.5, fontFamily: 'inherit' }}>
                  {estimate
                    ? `Te confirmamos el total con lo que cueste cada producto al comprarlo${ordering.service_fee && ordering.service_fee.type !== 'none' ? `, más ${(ordering.service_fee_label || 'la comisión').toLowerCase()}` : ''}. `
                    : ''}
                  {coveredByBalance
                    ? 'Se paga con tu saldo.'
                    : paysOnline
                      ? `Pagas ${fromBalance > 0 ? 'el resto ' : ''}ahora en línea. ${ordering.pay_online_note || ''}`.trim()
                      : `Pagas ${fromBalance > 0 ? 'el resto ' : ''}${fulfillment === 'delivery' ? 'al recibir tu pedido' : 'al recogerlo'}.`}
                </Typography>
              </Box>
              {ordering.pay_online && !estimate && !coveredByBalance && (
                <Box sx={{ mt: 2 }} data-testid="pay-choice">
                  <Typography sx={{ fontWeight: 700, fontFamily: 'inherit', mb: 1 }}>{fromBalance > 0 ? '¿Cómo pagas el resto?' : '¿Cómo pagas?'}</Typography>
                  {payRequired
                    ? <Typography sx={{ fontSize: 14, fontFamily: 'inherit' }}>
                        {fulfillment === 'delivery' ? 'Los pedidos a domicilio se pagan en línea' : 'Los pedidos se pagan en línea'}.{' '}{ordering.pay_online_note || ''}
                      </Typography>
                    : <Box sx={{ display: 'flex', gap: 1 }}>
                        {[payChoice('online', 'Pagar ahora'), payChoice('later', fulfillment === 'delivery' ? 'Al recibirlo' : 'Al recogerlo')]}
                      </Box>}
                </Box>
              )}
              {short && (
                <Typography sx={{ color: '#B26A00', fontSize: 14, mt: 1.5, fontFamily: 'inherit' }}>
                  El pedido mínimo es de {currency(ordering.min_order ?? 0)}.
                </Typography>
              )}
              {(errors.detail || errors.items) && (
                <Typography role="alert" sx={{ color: '#C0392B', fontSize: 14, mt: 1.5, fontFamily: 'inherit' }}>{errors.detail || errors.items}</Typography>
              )}
              <Button type="submit" fullWidth variant="contained" disabled={sending || short || !current} data-testid="send-order"
                sx={{ mt: 2, py: 1.5, borderRadius: 99, textTransform: 'none', fontWeight: 700, fontSize: 16, fontFamily: 'inherit',
                  bgcolor: c.primary, color: c.on_primary, boxShadow: 'none', '&:hover': { bgcolor: c.primary, opacity: 0.9, boxShadow: 'none' } }}>
                {sending ? 'Enviando…' : cart.unpriced ? 'Enviar pedido'
                  : coveredByBalance ? `Pagar ${currency(total)} con mi saldo`
                    : paysOnline ? `Pagar ${currency(rest)}` : `Enviar pedido · ${estimate ? 'aprox. ' : ''}${currency(rest)}`}
              </Button>
              {ordering.message && <Typography sx={{ color: c.muted, fontSize: 13.5, mt: 1.5, fontFamily: 'inherit' }}>{ordering.message}</Typography>}
            </Box>
          )}
        </Box>
      </Box>
    </Dialog>
  );
};

export default CartDialog;
